<template>
  <li @click="dumpContext" class="wt-video-preview-item">
    <transition name="wt2-fade" @afterLeave="afterLeave">
      <div v-if="!deleted">
        <div class="wt2-poster">
          <div
            v-if="!showAction && !isLocked"
            class="wt2-thumbnail-hover"
            :data-id="story.id"
            @click="navigateToStory"
          >
            <div v-if="!read" class="-badge -new-badge">
              {{ 'generic.NEW' | culture }}
            </div>
            <div class="-info">
              <i v-if="story.type === 'VO'" class="wticon wticon-mic" />
              <span class="wt2-video-duration" v-text="duration" />
            </div>
            <div class="-open-menu js-stop" @click="selectCurrentStory">
              <i class="wticon wticon-dots-three-full js-stop" />
            </div>
            <i class="wticon wticon-play3" />
          </div>

          <card-actions
            v-if="isSelected && showAction === 'menu'"
            :data-id="story.id"
            @action="doAction"
          />

          <wt-copy-link
            class="wt2-thumbnail-panel"
            :data-id="story.id"
            :class="{ '-hide': showAction !== 'publish' }"
            @close="showAction = null"
          >
            <input
              ref="input"
              class="wt2-input-transparent"
              readonly
              :value="publishUrl"
            />
          </wt-copy-link>
          <wt-thumbnail-confirm
            v-if="showAction === 'confirm'"
            :message="'confirmDelete.message' | culture"
            @no="showAction = null"
            @yes="deleteVideo"
          />
          <div class="-inner">
            <img v-if="poster" :src="poster" />
            <div v-else-if="!isLocked" class="-no-thumb" />
          </div>
        </div>

        <footer class="wt-preview-item-footer">
          <div class="-top-line">
            <div
              class="wt2-timestamp wt2-text-s -divider"
              :title="lastUpdateFull"
              v-text="lastUpdate"
            />
            <wt-aspect-ratio-tag :id="story.aspectRatio" />
          </div>
          <div class="-bottom-line">
            <div
              class="wt2-title"
              :title="story.title"
              v-text="story.title || 'Untitled Video'"
            />
          </div>
        </footer>
      </div>
    </transition>
  </li>
</template>

<script>
import app from 'app';
import Analytics from 'c/WtAnalytics';
import ProducedPopup from 'v/Produced/ProducedPopup.vue';
import { DuplicateController } from 'c/DuplicateController';
import { mapGetters, mapMutations } from 'vuex';

export default {
  name: 'video-preview-item',

  props: {
    story: { type: Object, required: true },
    tab: { type: String },
  },

  data: function () {
    return {
      showAction: null,
      read: this.story.read,
      deleted: false,
      isLocked: !!this.story.locked,
    };
  },

  watch: {
    showAction() {
      this.$_lockAutorefresh();
    },

    isSelected(newVal) {
      if (!newVal) {
        this.showAction = null;
      }
    },
  },

  computed: {
    ...mapGetters(['selectedStory']),

    poster() {
      return this.mixinSrc(this.story.thumbnailPath, this.story.posterPath);
    },

    lastUpdate() {
      const millisecondsFromNow =
        this.$store.state.currentTime - this.story.lastUpdate;
      return app.TIME.getTimeAgo(millisecondsFromNow);
    },

    lastUpdateFull() {
      return app.moment(this.story.lastUpdate).format('MM/DD/YYYY hh:mma');
    },

    duration() {
      const videoDuration = window.parseFloat(this.story.videoDurationSec);
      const durationMs = Math.floor(videoDuration * 1000);

      return app.moment.utc(durationMs).format('m:ss');
    },

    publishUrl() {
      return app.generatePublishUrl(this.story.id);
    },

    isSelected() {
      return this.selectedStory === this.story.id;
    },
  },

  methods: {
    ...mapMutations(['setSelectedStory']),

    $_lockAutorefresh() {
      this.$store.state.dashboard.lock.autorefresh = this.showAction !== null;
    },

    /**
     * @param e {Event}
     */
    dumpContext(e) {
      if (e.metaKey || e.ctrlKey) {
        this.$log('Story preview', this.story);
      }
    },

    /**
     * @param [e] {Event}
     */
    navigateToStory(e) {
      if (e && (e.metaKey || e.ctrlKey)) {
        return;
      }

      if (this.isLocked || e.target.classList.contains('js-stop')) {
        return;
      }

      Analytics.trigger('analytics:dashboard:playProducedVideo', {
        tab: this.tab,
      });

      this.mixinShowViewInModal({
        type: 'fullscreen',
        component: ProducedPopup,
        componentPropsData: {
          video: this.story,
        },
        showCloseButton: false,
        analytics({ action, params } = {}) {
          app.analytics('produce:' + action, params);
        },
      });
      this.read = true;
    },

    selectCurrentStory() {
      this.showAction = 'menu';
      this.setSelectedStory(this.story.id);
    },

    doAction: function ({ type }) {
      if (type === 'close') {
        this.showAction = null;
      } else if (type === 'edit') {
        this.navigateToStory();
      } else if (type === 'delete') {
        this.showAction = 'confirm';
      } else if (type === 'publish') {
        this.showAction = 'publish';
        this.$refs.input.select();
        document.execCommand('copy');
      } else if (type === 'duplicate') {
        DuplicateController.openDialog({
          story: this.story,
          source: 'dashboard',
          message: app.culture.makeCopyDialog.messageVideo,
          storyStartPoint: app.storyStartPoint.BACK_TO,
        }).then((data) => {
          if (data.changeOrg) {
            app.delayedSpinner.hide();
          } else {
            app.navigate(`stories/${data.storyId}`);
          }
        });
      }
    },

    afterLeave() {
      if (!this.deleted) {
        // shouldn't happen, just a safety measure
        return;
      }
      const id = this.story.id;
      Analytics.trigger('analytics:dashboard:deleteVideo', { tab: this.tab });
      app.api.producedStories
        .delete({ id })
        .fail(() => console.error(`failed deleting story ${id}`))
        .done(() => {
          console.log(`video ${id} deleted successfully`);
          this.$emit('itemRemoved');
        });

      this.$store.commit('deleteVideo', this.story.id);
    },

    deleteVideo() {
      this.deleted = true;
    },
  },

  components: {
    cardActions: {
      template: `
<div class="wt2-story-actions wt2-thumbnail-panel">
  <wt-close @click.stop.native="close"></wt-close>
  <span @click="publish"><i class="wticon wticon-link2"></i>{{'storyPreview.link' | culture}}</span>
  <span :class="{ disabled: !isUserCanCreateStories }" @click="duplicate"><i class="wticon wticon-duplicate"></i>{{'storyPreview.copy' | culture}}</span>
  <span :class="{ disabled: !isUserCanCreateStories }" @click="deleteItem"><i class="wticon wticon-trash2"></i>{{'storyPreview.remove' | culture}}</span>
</div>`.normalizeSpace(),

      data() {
        return {
          visible: true,
        };
      },

      computed: {
        ...mapGetters({
          isUserCanCreateStories: 'isUserCanCreateStories',
          userType: 'user/type',
        }),
      },

      methods: {
        close() {
          this.$emit('action', { type: 'close' });
        },
        publish() {
          this.$emit('action', { type: 'publish' });
        },
        deleteItem() {
          this.$emit('action', { type: 'delete' });
        },
        duplicate() {
          this.$emit('action', { type: 'duplicate' });
        },
      },
    },
  },
};
</script>
